import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Grid,
  Button,
  TextField,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import DatePicker from "react-datepicker";

import ListIcon from "@mui/icons-material/List";
import MapIcon from "@mui/icons-material/Map";
import EditIcon from "@mui/icons-material/Edit";
import ReservationChat from "./ReservationChat/ReservationChat";
import styles from "./Scheduling.module.css";
import { toast } from "react-toastify";
import { DateTime } from "luxon";

const Scheduling = ({ userDetails }) => {
  // console.log(userDetails);
  const navigate = useNavigate();
  const [isProfessional, setIsProfessional] = useState(false);
  const [viewMode, setViewMode] = useState("sentReservation"); // Toggle between "sentReservation" and "receiveReservation"
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);

  const [openProperties, setOpenProperties] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [sentReservations, setSentReservations] = useState([]);

  const [rejectionReasons, setRejectionReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingReservation, setEditingReservation] = useState(null);

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formatForInput = (dt) => dt.toFormat("yyyy-LL-dd'T'HH:mm");

  const now = DateTime.now().setZone(userTimeZone);

  function formatMoney(number) {
    if (typeof number === "number") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(number);
    }
    return number;
  }

  const handleToggleChange = (event, newView) => {
    if (newView) {
      setViewMode(newView);
    }
  };

  const openChat = (reservation) => {
    setSelectedReservation(reservation);
    setChatOpen(true);
  };

  const closeChat = () => {
    setSelectedReservation(null);
    setChatOpen(false);
  };

  const fetchSentReservations = async () => {
    try {
      setLoading(true);

      const sentReservationsResponse = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/reservation/sent-reservations?user_id=${userDetails.id}`
      );

      const sentReservationsData = sentReservationsResponse.data;

      if (sentReservationsData.success) {
        const sortedSentReservations = sentReservationsData.data.sort(
          (a, b) => {
            const dateA = new Date(a.updated_at || a.created_at);
            const dateB = new Date(b.updated_at || b.created_at);
            return dateB - dateA; // Sort descending
          }
        );
        setSentReservations(sortedSentReservations);
      } else {
        console.error("Error fetching sent reservations:");
      }
    } catch (err) {
      console.error("Error fetching sent reservations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchOpenProperties = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/professional/open-property?proSlug=${userDetails.id}`
        );

        const data = response.data;
        if (data.success) {
          setOpenProperties(data.data);
        } else {
          console.error("Unable to fetch open properties:", data.error);
        }
      } catch (error) {
        console.error("Error fetching open properties:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchReservations = async () => {
      try {
        setLoading(true);

        const reservationsResponse = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/reservation?user_id=${
            userDetails.id
          }`
        );

        const reservationsData = reservationsResponse.data;

        if (reservationsData.success) {
          const sortedReservations = reservationsData.data.sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at);
            const dateB = new Date(b.updated_at || b.created_at);
            return dateB - dateA; // Sort descending
          });
          setReservations(sortedReservations);
        }
      } catch (err) {
        console.error("Error fetching reservations:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserRole = async () => {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/auth/role-verification`,
        { email: userDetails.email }
      );
      // console.log("RWS", response);
      setIsProfessional(response.data.isProfessional);
    };

    fetchSentReservations();
    fetchReservations();
    fetchOpenProperties();
    fetchUserRole();
  }, [userDetails.id]);

  const handleEdit = (url) => {
    navigate(`/edit-listing/${url}`);
  };

  const handleAcceptReservation = async (reservationId) => {
    try {
      await axios.put(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/reservation/${reservationId}/accept`
      );
      toast.success("Reservation accepted successfully!");
      setReservations((prev) =>
        prev.map((res) =>
          res.id === reservationId ? { ...res, status: "accepted" } : res
        )
      );
    } catch (err) {
      console.error("Error accepting reservation:", err);
      toast.error("Failed to accept reservation.");
    }
  };

  const handleRejectReservation = async (reservationId) => {
    const reason = rejectionReasons[reservationId];
    if (!reason || reason.trim() === "") {
      setRejectionReasons((prev) => ({
        ...prev,
        [reservationId]: "Reason is required.",
      }));
      return;
    }

    try {
      await axios.put(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/reservation/${reservationId}/reject`,
        { reason }
      );
      toast.success("Reservation rejected successfully!");
      setReservations((prev) =>
        prev.map((res) =>
          res.id === reservationId ? { ...res, status: "rejected" } : res
        )
      );
      setRejectionReasons((prev) => ({ ...prev, [reservationId]: null }));
    } catch (err) {
      console.error("Error rejecting reservation:", err);
      toast.error("Failed to reject reservation.");
    }
  };

  const handleEditReservation = (reservation) => {
    setEditingReservation({
      ...reservation,
      booking_start_date: new Date(reservation.booking_start_date),
      booking_end_date: new Date(reservation.booking_end_date),
    });
  };

  const handleSaveReservation = async () => {
    try {
      const { id, booking_start_date, booking_end_date } = editingReservation;

      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/reservation/${id}/edit`,
        {
          booking_start_date: booking_start_date.toISOString(),
          booking_end_date: booking_end_date.toISOString(),
        }
      );

      if (response.data.success) {
        toast.success("Reservation updated successfully!");
        fetchSentReservations();
        setEditingReservation(null);
      } else {
        toast.error(response.data.error || "Failed to update the reservation.");
      }
    } catch (err) {
      console.error("Error updating reservation:", err);
      toast.error("An error occurred while updating the reservation.");
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    try {
      const response = await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/reservation/${reservationId}`,
        { data: { user_id: userDetails.id } }
      );

      if (response.data.success) {
        toast.success("Reservation deleted successfully.");
        setReservations((prev) =>
          prev.filter((res) => res.id !== reservationId)
        );
        setSentReservations((prev) =>
          prev.filter((res) => res.id !== reservationId)
        );
      } else {
        toast.error(response.data.error || "Failed to delete reservation.");
      }
    } catch (err) {
      console.error("Error deleting reservation:", err);
      toast.error(err.response.data.error || "Failed to delete reservation.");
    }
  };

  const generateDynamicUrl = (listing) => {
    const baseUrl = listing.url || "";
    if (listing.sell_price) return `/Buy-Homes/${baseUrl}`;
    if (listing.rent_price) return `/Rent-Homes/${baseUrl}`;
    return `/${baseUrl}`;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={4}>
      {isProfessional && (
        <>
          <div className={styles.containerTitle}>
            <h1>Open Houses</h1>
          </div>

          {openProperties.length === 0 ? (
            <Typography>No open properties available.</Typography>
          ) : (
            openProperties.map((property) => (
              <Paper key={property.id} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">
                  {property.listing_details.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {property.listing_details.description}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body1">
                      <strong>From:</strong>{" "}
                      {new Date(property.open_from).toLocaleString()}
                    </Typography>
                    <Typography variant="body1">
                      <strong>To:</strong>{" "}
                      {new Date(property.open_to).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1">
                      {property.listing_details.rent_price
                        ? formatMoney(property.listing_details.rent_price) +
                          " /mo"
                        : property.listing_details.sell_price
                        ? formatMoney(property.listing_details.sell_price)
                        : "Price Not Available"}
                    </Typography>
                  </Grid>
                </Grid>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <IconButton
                    color="primary"
                    onClick={() => handleEdit(property.listing_details.url)}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))
          )}
        </>
      )}

      <Grid container justifyContent="center" sx={{ marginBottom: "30px" }}>
        <div className={styles.toggleContainer}>
          <Button
            variant={viewMode === "sentReservation" ? "contained" : "outlined"}
            onClick={() => setViewMode("sentReservation")}
            className={
              viewMode === "sentReservation" ? styles.listSelected : ""
            }
          >
            <ListIcon />
            Sent Reservation
          </Button>
          <Button
            variant={
              viewMode === "receiveReservation" ? "contained" : "outlined"
            }
            onClick={() => setViewMode("receiveReservation")}
            className={
              viewMode === "receiveReservation" ? styles.mapSelected : ""
            }
          >
            <MapIcon />
            Receive Reservation
          </Button>
        </div>
      </Grid>

      {viewMode === "sentReservation" ? (
        <>
          <div className={styles.containerTitle}>
            <h1>Sent Reservations</h1>
          </div>

          {sentReservations.length === 0 ? (
            <Typography>No sent reservations found.</Typography>
          ) : (
            sentReservations.map((reservation) => (
              <Paper key={reservation.id} className={styles.reservationCard}>
                <Typography className={styles.reservationHeader}>
                  Reservation ID: {reservation.id}
                </Typography>
                <div className={styles.reservationDetails}>
                  <Typography>
                    <strong>Reservation Date:</strong>{" "}
                    {`${new Date(
                      reservation.booking_start_date
                    ).toLocaleString()} - ${new Date(
                      reservation.booking_end_date
                    ).toLocaleString()}`}
                  </Typography>
                  <Typography>
                    <strong>Status:</strong> {reservation.status}
                  </Typography>
                  <Typography>
                    <strong>Reservation Message:</strong>{" "}
                    {reservation.message || "N/A"}
                  </Typography>
                </div>
                <Typography variant="h6" gutterBottom>
                  Listing Details
                </Typography>
                <div className={styles.reservationDetails}>
                  <Typography>
                    <strong>Title:</strong>{" "}
                    {reservation?.listing_title || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Description:</strong>{" "}
                    {reservation?.listing_description || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Price:</strong>{" "}
                    {reservation?.sell_price
                      ? formatMoney(reservation.sell_price)
                      : "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Type:</strong> {reservation?.listing_type || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>URL:</strong>{" "}
                    <a
                      href={generateDynamicUrl(reservation)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Listing
                    </a>
                  </Typography>
                </div>

                {reservation.status === "rejected" && (
                  <div className={styles.rejectionReason}>
                    <Typography color="error">
                      <strong>Rejection Reason:</strong>{" "}
                      {reservation.reject_reason}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleEditReservation(reservation)}
                      sx={{ mt: 1 }}
                    >
                      Edit and Resubmit
                    </Button>
                  </div>
                )}
                <div className={styles.actionButtons}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => openChat(reservation)}
                  >
                    Open Chat
                  </Button>

                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleDeleteReservation(reservation.id)}
                  >
                    Delete Reservation
                  </Button>
                </div>
              </Paper>
            ))
          )}

          <Modal
            open={!!editingReservation}
            onClose={() => setEditingReservation(null)}
            aria-labelledby="edit-reservation-modal-title"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 600,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                id="edit-reservation-modal-title"
              >
                Edit Reservation
              </Typography>
              <div className={styles.datePickerContainer}>
                <div className={styles.datePicker}>
                  <TextField
                    label="Start Date & Time"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={
                      editingReservation?.booking_start_date
                        ? formatForInput(
                            DateTime.fromJSDate(
                              editingReservation.booking_start_date
                            ).setZone(userTimeZone)
                          )
                        : formatForInput(now)
                    }
                    onChange={(e) =>
                      setEditingReservation((prev) => ({
                        ...prev,
                        booking_start_date: new Date(e.target.value),
                      }))
                    }
                    inputProps={{
                      min: formatForInput(now),
                    }}
                    helperText={
                      DateTime.fromISO(editingReservation?.booking_start_date) <
                      now
                        ? "Start cannot be in the past"
                        : ""
                    }
                    error={
                      DateTime.fromISO(editingReservation?.booking_start_date) <
                      now
                    }
                  />
                </div>

                <div className={styles.datePicker}>
                  <TextField
                    label="End Date & Time"
                    type="datetime-local"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={
                      editingReservation?.booking_end_date
                        ? formatForInput(
                            DateTime.fromJSDate(
                              editingReservation.booking_end_date
                            ).setZone(userTimeZone)
                          )
                        : formatForInput(now.plus({ hours: 2 }))
                    }
                    onChange={(e) =>
                      setEditingReservation((prev) => ({
                        ...prev,
                        booking_end_date: new Date(e.target.value),
                      }))
                    }
                    inputProps={{
                      min: editingReservation?.booking_start_date,
                    }}
                    helperText={
                      DateTime.fromISO(editingReservation?.booking_end_date) <=
                      DateTime.fromISO(editingReservation?.booking_start_date)
                        ? "End must be after start"
                        : ""
                    }
                    error={
                      DateTime.fromISO(editingReservation?.booking_end_date) <=
                      DateTime.fromISO(editingReservation?.booking_start_date)
                    }
                  />
                </div>
              </div>

              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveReservation}
                sx={{ mt: 2 }}
              >
                Save and Resubmit
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setEditingReservation(null)}
                sx={{ mt: 2, ml: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </Modal>
        </>
      ) : (
        <>
          <div className={styles.containerTitle}>
            <h1>Received Reservations</h1>
          </div>

          {reservations.length === 0 ? (
            <Typography>No reservations found.</Typography>
          ) : (
            reservations.map((reservation) => (
              <Paper key={reservation.id} className={styles.reservationCard}>
                <Typography className={styles.reservationHeader}>
                  Reservation ID: {reservation.id}
                </Typography>
                <div className={styles.reservationDetails}>
                  <Typography>
                    <strong>Reservation Date:</strong>{" "}
                    {`${new Date(
                      reservation.booking_start_date
                    ).toLocaleString()} - ${new Date(
                      reservation.booking_end_date
                    ).toLocaleString()}`}
                  </Typography>
                  <Typography>
                    <strong>Status:</strong> {reservation.status}
                  </Typography>

                  <Typography>
                    <strong> Reservation Message:</strong>{" "}
                    {reservation.message || "N/A"}
                  </Typography>
                </div>
                <Typography variant="h6" gutterBottom>
                  Listing Details
                </Typography>
                <div className={styles.reservationDetails}>
                  <Typography>
                    <strong>Title:</strong>{" "}
                    {reservation.listing_details?.title || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Description:</strong>{" "}
                    {reservation.listing_details?.description || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Price:</strong>{" "}
                    {reservation.listing_details?.sell_price
                      ? formatMoney(reservation.listing_details.sell_price)
                      : "N/A"}
                  </Typography>
                  <Typography>
                    <strong>Type:</strong>{" "}
                    {reservation.listing_details?.listing_type || "N/A"}
                  </Typography>
                  <Typography>
                    <strong>URL:</strong>{" "}
                    <a
                      href={generateDynamicUrl(reservation.listing_details)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Listing
                    </a>
                  </Typography>
                </div>

                {reservation.status === "pending" && (
                  <div className={styles.rejectionReason}>
                    <TextField
                      label="Reason for Rejection"
                      variant="outlined"
                      multiline
                      rows={3}
                      fullWidth
                      value={rejectionReasons[reservation.id] || ""}
                      onChange={(e) =>
                        setRejectionReasons({
                          ...rejectionReasons,
                          [reservation.id]: e.target.value,
                        })
                      }
                      error={
                        rejectionReasons[reservation.id] === undefined ||
                        rejectionReasons[reservation.id].trim() === ""
                      }
                      helperText={
                        rejectionReasons[reservation.id] === undefined ||
                        rejectionReasons[reservation.id].trim() === ""
                          ? "Rejection reason is required"
                          : ""
                      }
                    />
                  </div>
                )}

                <div className={styles.actionButtons}>
                  {reservation.status === "pending" && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAcceptReservation(reservation.id)}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                          if (
                            !rejectionReasons[reservation.id] ||
                            rejectionReasons[reservation.id].trim() === ""
                          ) {
                            toast.error("Rejection reason is required.");
                            return;
                          }
                          handleRejectReservation(
                            reservation.id,
                            rejectionReasons[reservation.id]
                          );
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => openChat(reservation)}
                  >
                    Open Chat
                  </Button>

                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleDeleteReservation(reservation.id)}
                  >
                    Delete Reservation
                  </Button>
                </div>
              </Paper>
            ))
          )}
        </>
      )}
      <Modal
        open={chatOpen}
        onClose={closeChat}
        aria-labelledby="chat-modal-title"
        aria-describedby="chat-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography id="chat-modal-title" variant="h5" gutterBottom>
            Chat for Reservation ID: {selectedReservation?.id}
          </Typography>
          <ReservationChat
            reservation={selectedReservation}
            userDetails={userDetails}
          />
          <Button
            variant="outlined"
            color="error"
            onClick={closeChat}
            sx={{ mt: 2 }}
          >
            Close Chat
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default Scheduling;
